// src/components/Reports/CustomReportBuilder.js
import React, { useState, useEffect } from "react";
import { localYmd } from '../../utils/dateOnly';
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { ProgressBar } from "primereact/progressbar";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import axiosInstance from "../../services/axiosInstance";
import { handleApiError, handleApiSuccess } from "../apiErrorHandler";

const toISO = (d) => (d instanceof Date ? localYmd(d) : d ?? "");

// ─── FilterValueInput ──────────────────────────────────────────────────────

const FilterValueInput = ({ filterDef, operator, value, onChange }) => {
    if (!filterDef) return null;
    const { type, choices = [] } = filterDef;

    if (operator === "range") {
        const from = Array.isArray(value) ? value[0] : null;
        const to   = Array.isArray(value) ? value[1] : null;
        return (
            <div className="flex align-items-center gap-1">
                <Calendar
                    value={from ? new Date(from) : null}
                    onChange={(e) => onChange([toISO(e.value), to])}
                    dateFormat="yy-mm-dd" showIcon placeholder="From"
                    style={{ width: 155 }}
                />
                <span className="text-400 text-sm">–</span>
                <Calendar
                    value={to ? new Date(to) : null}
                    onChange={(e) => onChange([from, toISO(e.value)])}
                    dateFormat="yy-mm-dd" showIcon placeholder="To"
                    style={{ width: 155 }}
                />
            </div>
        );
    }

    if (type === "choice") {
        return (
            <Dropdown
                value={value ?? null}
                options={choices.map((c) => ({ label: c, value: c }))}
                onChange={(e) => onChange(e.value)}
                placeholder="Select…"
                style={{ width: 180 }}
            />
        );
    }

    if (type === "boolean") {
        return (
            <Dropdown
                value={value ?? null}
                options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
                onChange={(e) => onChange(e.value)}
                style={{ width: 140 }}
            />
        );
    }

    if (type === "date" || type === "datetime") {
        return (
            <Calendar
                value={value ? new Date(value) : null}
                onChange={(e) => onChange(toISO(e.value))}
                dateFormat="yy-mm-dd" showIcon
                style={{ width: 175 }}
            />
        );
    }

    if (type === "decimal" || type === "integer") {
        return (
            <InputNumber
                value={value ?? null}
                onValueChange={(e) => onChange(e.value)}
                minFractionDigits={type === "decimal" ? 2 : 0}
                maxFractionDigits={type === "decimal" ? 2 : 0}
                style={{ width: 140 }}
            />
        );
    }

    return (
        <InputText
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Value…"
            style={{ width: 180 }}
        />
    );
};

// ─── FilterRow ─────────────────────────────────────────────────────────────

const FilterRow = ({ filter, filterDefs, onChange, onRemove }) => {
    const filterDef = filterDefs.find((f) => f.key === filter.key);
    const operators = filterDef?.operators || [];

    const handleKeyChange = (newKey) => {
        const newDef   = filterDefs.find((f) => f.key === newKey);
        const defaultOp = newDef?.operators?.[0]?.value ?? "eq";
        onChange({ key: newKey, operator: defaultOp, value: null });
    };

    const handleOpChange = (newOp) => {
        const wasRange = filter.operator === "range";
        const isRange  = newOp === "range";
        onChange({ ...filter, operator: newOp, value: (wasRange !== isRange) ? null : filter.value });
    };

    return (
        <div className="flex align-items-center gap-2 mb-2 flex-wrap">
            <Dropdown
                value={filter.key}
                options={filterDefs.map((f) => ({ label: f.label, value: f.key }))}
                onChange={(e) => handleKeyChange(e.value)}
                placeholder="Field…"
                style={{ width: 185 }}
            />
            <Dropdown
                value={filter.operator ?? operators[0]?.value}
                options={operators}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => handleOpChange(e.value)}
                style={{ width: 175 }}
                disabled={operators.length <= 1}
            />
            <FilterValueInput
                filterDef={filterDef}
                operator={filter.operator ?? operators[0]?.value}
                value={filter.value}
                onChange={(v) => onChange({ ...filter, value: v })}
            />
            <Button
                icon="pi pi-times"
                className="p-button-text p-button-danger p-button-sm"
                onClick={onRemove}
                tooltip="Remove filter"
            />
        </div>
    );
};

// ─── SaveTemplateDialog ────────────────────────────────────────────────────

const SaveTemplateDialog = ({ visible, onHide, onSave, source, columns, filters }) => {
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try {
            await axiosInstance.post("/api/reports/v2/custom/templates/", {
                name: name.trim(), source, columns, filters,
            });
            handleApiSuccess("Template saved.");
            setName("");
            onSave();
            onHide();
        } catch (err) {
            handleApiError(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog header="Save as Template" visible={visible} onHide={onHide} style={{ width: 400 }}>
            <div className="field">
                <label className="font-bold block mb-2">Template Name</label>
                <InputText
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Monthly Sales by Product"
                    className="w-full"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && save()}
                />
            </div>
            <div className="flex justify-content-end gap-2 mt-3">
                <Button label="Cancel" className="p-button-text" onClick={onHide} />
                <Button
                    label="Save"
                    icon={saving ? "pi pi-spin pi-spinner" : "pi pi-save"}
                    disabled={saving || !name.trim()}
                    onClick={save}
                />
            </div>
        </Dialog>
    );
};

// ─── CustomReportBuilder ───────────────────────────────────────────────────

const CustomReportBuilder = () => {
    const [schema, setSchema]               = useState(null);
    const [schemaLoading, setSchemaLoading] = useState(true);
    const [source, setSource]               = useState(null);
    const [columns, setColumns]             = useState([]);
    const [filters, setFilters]             = useState([]);
    const [sortBy, setSortBy]               = useState(null);
    const [sortDir, setSortDir]             = useState("asc");
    const [loading, setLoading]             = useState(false);
    const [result, setResult]               = useState(null);
    const [error, setError]                 = useState(null);
    const [templates, setTemplates]         = useState([]);
    const [saveDialogVisible, setSaveDialogVisible]         = useState(false);
    const [templatesDialogVisible, setTemplatesDialogVisible] = useState(false);

    useEffect(() => {
        axiosInstance.get("/api/reports/v2/custom/schema/")
            .then((res) => setSchema(res.data))
            .catch(handleApiError)
            .finally(() => setSchemaLoading(false));
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        try {
            const res = await axiosInstance.get("/api/reports/v2/custom/templates/");
            setTemplates(res.data.data || []);
        } catch { /* silent */ }
    };

    const sourceDef     = schema?.sources?.find((s) => s.id === source);
    const columnOptions = (sourceDef?.columns || []).map((c) => ({ label: c.label, value: c.key }));
    const filterDefs    = sourceDef?.filters || [];

    const handleSourceChange = (val) => {
        setSource(val); setColumns([]); setFilters([]);
        setSortBy(null); setResult(null); setError(null);
    };

    const addFilter = () => {
        if (!filterDefs.length) return;
        const first = filterDefs[0];
        setFilters((prev) => [...prev, {
            key:      first.key,
            operator: first.operators?.[0]?.value ?? "eq",
            value:    null,
        }]);
    };

    const activeFilters = filters.filter((f) => {
        if (!f.key || f.value === null || f.value === "") return false;
        if (f.operator === "range") return Array.isArray(f.value) && f.value[0] && f.value[1];
        return true;
    });

    const runReport = async () => {
        if (!source || !columns.length) return;
        setLoading(true); setError(null); setResult(null);
        try {
            const res = await axiosInstance.post("/api/reports/v2/custom/run/", {
                source, columns,
                filters: activeFilters,
                sort_by:  sortBy || undefined,
                sort_dir: sortDir,
            });
            setResult(res.data);
        } catch (err) {
            setError("Failed to run report. Check your filters and try again.");
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    };

    const exportCsv = async () => {
        try {
            const res = await axiosInstance.post(
                "/api/reports/v2/custom/run/",
                { source, columns, filters: activeFilters, sort_by: sortBy, sort_dir: sortDir, export: true },
                { responseType: "blob" }
            );
            const blob = new Blob([res.data], { type: "text/csv" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `custom-report-${source}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (err) { handleApiError(err); }
    };

    const loadTemplate = (tpl) => {
        setSource(tpl.source); setColumns(tpl.columns);
        setFilters(tpl.filters || []); setSortBy(null);
        setResult(null); setError(null);
        setTemplatesDialogVisible(false);
    };

    const deleteTemplate = async (id) => {
        try {
            await axiosInstance.delete(`/api/reports/v2/custom/templates/${id}/`);
            await loadTemplates();
            handleApiSuccess("Template deleted.");
        } catch (err) { handleApiError(err); }
    };

    const resultData    = result?.data || [];
    const resultColumns = result?.columns || columns;
    const colLabelMap   = Object.fromEntries(
        (sourceDef?.columns || []).map((c) => [c.key, c.label])
    );

    if (schemaLoading) return <ProgressBar mode="indeterminate" style={{ height: 4 }} />;

    const stepCircle = (n) => (
        <span className="inline-flex align-items-center justify-content-center border-circle bg-primary text-white mr-2"
            style={{ width: 20, height: 20, fontSize: 11 }}>{n}</span>
    );

    return (
        <div>
            <div className="flex justify-content-between align-items-center mb-3">
                <p className="text-600 text-sm m-0">
                    Pick a data source, choose columns, add filters, then run.
                </p>
                <Button
                    label="Saved Templates"
                    icon="pi pi-bookmark"
                    className="p-button-sm p-button-outlined"
                    onClick={() => setTemplatesDialogVisible(true)}
                    badge={templates.length > 0 ? String(templates.length) : undefined}
                />
            </div>

            <div className="grid">
                {/* Builder */}
                <div className="col-12 lg:col-5">
                    <Card className="shadow-1 border-round-lg">
                        <h4 className="mt-0 mb-3 text-800 font-semibold">
                            <i className="pi pi-sliders-h mr-2 text-primary" />Build Query
                        </h4>

                        {/* 1 — Source */}
                        <div className="mb-4">
                            <label className="block font-semibold text-sm mb-2">{stepCircle(1)}Data Source</label>
                            <Dropdown
                                value={source}
                                options={(schema?.sources || []).map((s) => ({ label: s.label, value: s.id }))}
                                onChange={(e) => handleSourceChange(e.value)}
                                placeholder="Select a data source…"
                                className="w-full"
                            />
                        </div>

                        {/* 2 — Columns */}
                        {source && (
                            <div className="mb-4">
                                <label className="block font-semibold text-sm mb-2">{stepCircle(2)}Columns</label>
                                <MultiSelect
                                    value={columns}
                                    options={columnOptions}
                                    onChange={(e) => setColumns(e.value)}
                                    placeholder="Select columns…"
                                    display="chip"
                                    className="w-full"
                                    filter
                                />
                                {columns.length > 0 && (
                                    <div className="text-xs text-500 mt-1">
                                        {columns.length} column{columns.length !== 1 ? "s" : ""} selected
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 3 — Filters */}
                        {source && (
                            <div className="mb-4">
                                <div className="flex align-items-center justify-content-between mb-2">
                                    <label className="font-semibold text-sm">
                                        {stepCircle(3)}Filters
                                        <span className="text-400 font-normal ml-1">(optional)</span>
                                    </label>
                                    <Button label="Add Filter" icon="pi pi-plus"
                                        className="p-button-text p-button-sm"
                                        onClick={addFilter} disabled={!filterDefs.length} />
                                </div>
                                {filters.length === 0 && (
                                    <div className="text-sm text-400 py-2">
                                        No filters — all records returned (up to row limit).
                                    </div>
                                )}
                                {filters.map((f, idx) => (
                                    <FilterRow
                                        key={idx}
                                        filter={f}
                                        filterDefs={filterDefs}
                                        onChange={(updated) => setFilters((prev) => prev.map((x, i) => i === idx ? updated : x))}
                                        onRemove={() => setFilters((prev) => prev.filter((_, i) => i !== idx))}
                                    />
                                ))}
                            </div>
                        )}

                        {/* 4 — Sort */}
                        {source && columns.length > 0 && (
                            <div className="mb-4">
                                <label className="block font-semibold text-sm mb-2">
                                    {stepCircle(4)}Sort
                                    <span className="text-400 font-normal ml-1">(optional)</span>
                                </label>
                                <div className="flex gap-2">
                                    <Dropdown
                                        value={sortBy}
                                        options={[{ label: "— none —", value: null },
                                            ...columnOptions.filter((o) => columns.includes(o.value))]}
                                        onChange={(e) => setSortBy(e.value)}
                                        style={{ flex: 1 }}
                                    />
                                    <Dropdown
                                        value={sortDir}
                                        options={[{ label: "Asc ↑", value: "asc" }, { label: "Desc ↓", value: "desc" }]}
                                        onChange={(e) => setSortDir(e.value)}
                                        style={{ width: 110 }}
                                        disabled={!sortBy}
                                    />
                                </div>
                            </div>
                        )}

                        <Divider />

                        <div className="flex gap-2 flex-wrap">
                            <Button
                                label="Run Report"
                                icon={loading ? "pi pi-spin pi-spinner" : "pi pi-play"}
                                disabled={loading || !source || !columns.length}
                                onClick={runReport}
                            />
                            {resultData.length > 0 && (
                                <>
                                    <Button label="Export CSV" icon="pi pi-download"
                                        className="p-button-outlined p-button-secondary"
                                        onClick={exportCsv} />
                                    <Button label="Save Template" icon="pi pi-bookmark"
                                        className="p-button-outlined"
                                        onClick={() => setSaveDialogVisible(true)} />
                                </>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Results */}
                <div className="col-12 lg:col-7">
                    <Card className="shadow-1 border-round-lg">
                        <h4 className="mt-0 mb-3 text-800 font-semibold">
                            <i className="pi pi-table mr-2 text-primary" />Results
                            {result && (
                                <span className="ml-2 text-sm font-normal text-600">
                                    — {result.row_count} row{result.row_count !== 1 ? "s" : ""}
                                </span>
                            )}
                        </h4>

                        {error && <Message severity="error" text={error} className="w-full mb-3" />}
                        {loading && <ProgressBar mode="indeterminate" style={{ height: 4 }} className="mb-3" />}

                        {!result && !loading && (
                            <div className="flex flex-column align-items-center justify-content-center py-6 text-400">
                                <i className="pi pi-table text-5xl mb-3 text-300" />
                                <span className="text-sm">Results will appear here after running the report.</span>
                            </div>
                        )}

                        {result && !loading && (
                            <DataTable
                                value={resultData}
                                paginator rows={20}
                                rowsPerPageOptions={[20, 50, 100]}
                                size="small" stripedRows removableSort
                                scrollable scrollHeight="flex"
                                emptyMessage="No results found."
                            >
                                {resultColumns.map((col) => (
                                    <Column key={col} field={col}
                                        header={colLabelMap[col] || col} sortable />
                                ))}
                            </DataTable>
                        )}
                    </Card>
                </div>
            </div>

            <SaveTemplateDialog
                visible={saveDialogVisible}
                onHide={() => setSaveDialogVisible(false)}
                onSave={loadTemplates}
                source={source} columns={columns} filters={activeFilters}
            />

            <Dialog header="Saved Templates" visible={templatesDialogVisible}
                onHide={() => setTemplatesDialogVisible(false)} style={{ width: 580 }}>
                {templates.length === 0 ? (
                    <div className="text-center text-400 py-4">
                        <i className="pi pi-bookmark text-3xl mb-2 block" />
                        No saved templates yet.
                    </div>
                ) : (
                    <DataTable value={templates} size="small" stripedRows>
                        <Column field="name" header="Name" sortable />
                        <Column field="source" header="Source" sortable
                            body={(r) => <Tag value={r.source} severity="info" />} />
                        <Column field="created_by" header="Created By" />
                        <Column header="Actions" body={(r) => (
                            <div className="flex gap-1">
                                <Button icon="pi pi-play" className="p-button-text p-button-sm p-button-success"
                                    tooltip="Load" onClick={() => loadTemplate(r)} />
                                <Button icon="pi pi-trash" className="p-button-text p-button-sm p-button-danger"
                                    tooltip="Delete" onClick={() => deleteTemplate(r.id)} />
                            </div>
                        )} />
                    </DataTable>
                )}
            </Dialog>
        </div>
    );
};

export default CustomReportBuilder;